import {
  AccountCircle,
  AdminPanelSettings,
  AttachMoney,
  Attribution,
  BusinessCenter,
  CalendarMonth,
  CreditCard,
  Hive,
  Home,
  Person,
  PersonSearch,
  PieChart,
  StackedBarChart,
  WatchLater,
} from '@mui/icons-material'
import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import navigation from '../../config/navigation'
import { SidebarSection } from './sidebarUtils'
import { PageIndex } from '../../store/redux/reducers/pageNameSlice'
import RoleType, { RoleName } from '../../store/types/enums/RoleType'

export type SidebarLinkContext = {
  currentUserId?: number | string
  hideSchedule: boolean
}

export type SidebarLinkDefinition = {
  section: SidebarSection
  pageIndex: PageIndex
  icon: React.ElementType
  textKey: string
  getRoute: string | ((context: SidebarLinkContext) => string)
  allowedRoles?: RoleName[]
  isHidden?: (context: SidebarLinkContext) => boolean
}

const EMPLOYEE_MENU_ROLES: RoleName[] = [
  RoleType.Employee,
  RoleType.Leader,
  RoleType.ProjectLeader,
  RoleType.Management,
]

const LEADER_PROJECT_MANAGEMENT_ROLES: RoleName[] = [RoleType.Leader, RoleType.ProjectLeader, RoleType.Management]

const LEADER_PROJECT_MANAGEMENT_ADMIN_ROLES: RoleName[] = [...LEADER_PROJECT_MANAGEMENT_ROLES, RoleType.Admin]

const PROJECTS_SECTION_ROLES: RoleName[] = [...LEADER_PROJECT_MANAGEMENT_ROLES, RoleType.HR]

const RECRUITER_MANAGEMENT_ROLES: RoleName[] = [RoleType.Recruiter, RoleType.Management]

const MANAGEMENT_ADMIN_ROLES: RoleName[] = [RoleType.Management, RoleType.Admin]

const PROJECT_LEADER_MANAGEMENT_ROLES: RoleName[] = [RoleType.ProjectLeader, RoleType.Management]

const LEADER_MANAGEMENT_ROLES: RoleName[] = [RoleType.Leader, RoleType.Management]

const withUserId =
  (path: string) =>
  ({ currentUserId }: SidebarLinkContext) =>
    `${path}/${currentUserId ?? ''}`

export const SIDEBAR_LINK_DEFINITIONS: SidebarLinkDefinition[] = [
  {
    section: SidebarSection.MAIN,
    pageIndex: PageIndex.HOME,
    icon: Home,
    textKey: 'page.name.home',
    getRoute: navigation.home,
  },
  {
    section: SidebarSection.MAIN,
    pageIndex: PageIndex.SCHEDULE,
    icon: CalendarMonth,
    textKey: 'page.name.schedule',
    getRoute: withUserId(navigation.schedule),
    allowedRoles: EMPLOYEE_MENU_ROLES,
    isHidden: ({ hideSchedule }) => hideSchedule,
  },
  {
    section: SidebarSection.MAIN,
    pageIndex: PageIndex.WORKLOG,
    icon: WatchLater,
    textKey: 'page.name.worklog',
    getRoute: withUserId(navigation.worklog),
    allowedRoles: EMPLOYEE_MENU_ROLES,
  },
  {
    section: SidebarSection.MAIN,
    pageIndex: PageIndex.VACATION,
    icon: BeachAccessIcon,
    textKey: 'page.name.vacations',
    getRoute: navigation.vacation,
    allowedRoles: EMPLOYEE_MENU_ROLES,
  },
  {
    section: SidebarSection.MAIN,
    pageIndex: PageIndex.YEARLY_SUMMARY,
    icon: StackedBarChart,
    textKey: 'page.name.yearlySummary',
    getRoute: withUserId(navigation.yearlySummary),
    allowedRoles: EMPLOYEE_MENU_ROLES,
  },
  {
    section: SidebarSection.PROJECTS,
    pageIndex: PageIndex.PROJECTS,
    icon: Hive,
    textKey: 'page.name.projects',
    getRoute: navigation.projects,
    allowedRoles: LEADER_PROJECT_MANAGEMENT_ADMIN_ROLES,
  },
  {
    section: SidebarSection.PROJECTS,
    pageIndex: PageIndex.REPORTS,
    icon: PieChart,
    textKey: 'page.name.reports',
    getRoute: navigation.reports,
    allowedRoles: PROJECTS_SECTION_ROLES,
  },
  {
    section: SidebarSection.PROJECTS,
    pageIndex: PageIndex.LEADER_VIEW,
    icon: Hive,
    textKey: 'page.name.leaderView',
    getRoute: navigation.leaderView,
    allowedRoles: LEADER_PROJECT_MANAGEMENT_ROLES,
  },
  {
    section: SidebarSection.RESOURCING,
    pageIndex: PageIndex.PROJECTS_DISTRIBUTION,
    icon: Attribution,
    textKey: 'page.name.plan',
    getRoute: navigation.projectsDistribution,
    allowedRoles: PROJECT_LEADER_MANAGEMENT_ROLES,
  },
  {
    section: SidebarSection.RESOURCING,
    pageIndex: PageIndex.SETTLEMENTS,
    icon: CreditCard,
    textKey: 'page.name.settlements',
    getRoute: navigation.settlements,
    allowedRoles: LEADER_MANAGEMENT_ROLES,
  },
  {
    section: SidebarSection.RECRUITMENT,
    pageIndex: PageIndex.CANDIDATES,
    icon: BusinessCenter,
    textKey: 'page.name.candidates.candidates',
    getRoute: navigation.candidates,
    allowedRoles: RECRUITER_MANAGEMENT_ROLES,
  },
  {
    section: SidebarSection.ADMINISTRATION,
    pageIndex: PageIndex.ADMINS_PAGE,
    icon: AdminPanelSettings,
    textKey: 'page.name.adminSettings.settings',
    getRoute: navigation.general,
    allowedRoles: MANAGEMENT_ADMIN_ROLES,
  },
  {
    section: SidebarSection.ADMINISTRATION,
    pageIndex: PageIndex.USERS,
    icon: Person,
    textKey: 'page.name.users.users',
    getRoute: navigation.users,
    allowedRoles: LEADER_PROJECT_MANAGEMENT_ADMIN_ROLES,
  },
  {
    section: SidebarSection.ADMINISTRATION,
    pageIndex: PageIndex.DEMANDS,
    icon: PersonSearch,
    textKey: 'page.name.demands.demands',
    getRoute: navigation.demands,
    allowedRoles: PROJECT_LEADER_MANAGEMENT_ROLES,
  },
  {
    section: SidebarSection.ADMINISTRATION,
    pageIndex: PageIndex.COST_SUMMARY,
    icon: AttachMoney,
    textKey: 'page.name.costSummary',
    getRoute: navigation.costSummary,
    allowedRoles: MANAGEMENT_ADMIN_ROLES,
  },
  {
    section: SidebarSection.PERSONAL,
    pageIndex: PageIndex.PROFILE,
    icon: AccountCircle,
    textKey: 'page.name.myProfile',
    getRoute: navigation.myProfile,
  },
]
